/**
 * Shared iframe print orchestration for tool pages.
 *
 * Posts a form into a hidden iframe, waits for the print-layout page to load,
 * then triggers the browser print dialog — all without navigating away from
 * the current page.
 *
 * Usage:
 *   import { initIframePrint } from './shared/iframe-print';
 *   initIframePrint({ formId: 'efcPrintForm', iframeId: 'efcPrintFrame', buttonId: 'efcPrintBtn' });
 */

/** Configuration for a single iframe print binding. */
export interface IframePrintConfig {
    /** ID of the <form> that targets the iframe. */
    formId: string;
    /** ID of the hidden <iframe> that receives the POST. */
    iframeId: string;
    /** ID of the submit <button> (for loading state). */
    buttonId: string;
    /** Button label shown while the iframe is loading. */
    loadingLabel?: string;
    /** Button label restored after printing. */
    readyLabel?: string;
    /** Maximum time (ms) to wait before re-enabling the button. */
    timeoutMs?: number;
    /**
     * ID of a separate source form whose fields should be collected and
     * serialized as JSON into the hidden input identified by `inputFieldId`.
     * When set, the print form does not need its own hidden fields for each
     * data property — everything is read dynamically from the source form.
     */
    sourceFormId?: string;
    /**
     * ID of the hidden `<input>` inside the print form that receives the
     * JSON-serialized data collected from `sourceFormId`.
     * Required when `sourceFormId` is provided.
     */
    inputFieldId?: string;
    /**
     * Optional callback invoked before the form is submitted.
     * Use this for custom data gathering when the default `collectFormData`
     * is insufficient (e.g., for nested arrays like itinerary days).
     */
    beforeSubmit?: () => void;
}

const DEFAULTS = {
    loadingLabel: '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Preparing...',
    readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Shopping List',
    timeoutMs: 10_000,
    sourceFormId: '',
    inputFieldId: '',
} as const;

/** Resolved config with all required fields populated and optional callbacks. */
type ResolvedConfig = Required<Omit<IframePrintConfig, 'beforeSubmit'>> & Pick<IframePrintConfig, 'beforeSubmit'>;

/**
 * Convert a PascalCase or mixed-case string to camelCase.
 */
function toCamelCase(name: string): string {
    return name.charAt(0).toLowerCase() + name.slice(1);
}

/**
 * Collect all named fields from a form and return a plain object with
 * camelCase keys.  Handles inputs, textareas, selects, and radio groups.
 * Skips fields without a name and the anti-forgery token.
 */
function collectFormData(sourceForm: HTMLFormElement): Record<string, string> {
    const data: Record<string, string> = {};
    const formData = new FormData(sourceForm);

    formData.forEach((value, key) => {
        if (key === '__RequestVerificationToken') {
            return;
        }
        data[toCamelCase(key)] = value as string;
    });

    return data;
}

/**
 * Set the print button to a loading or ready state.
 */
function setButtonState(btn: HTMLButtonElement, loading: boolean, config: ResolvedConfig): void {
    btn.disabled = loading;
    btn.innerHTML = loading ? config.loadingLabel : config.readyLabel;
}

/**
 * Wire the hidden-iframe print flow for a single form/iframe pair.
 * Call once after the DOM is ready.
 */
export function initIframePrint(config: IframePrintConfig): void {
    const resolved: ResolvedConfig = {
        ...DEFAULTS,
        ...config,
    };

    const form = document.getElementById(resolved.formId) as HTMLFormElement | null;
    const iframe = document.getElementById(resolved.iframeId) as HTMLIFrameElement | null;
    const btn = document.getElementById(resolved.buttonId) as HTMLButtonElement | null;

    if (!form || !iframe) {
        return;
    }

    form.addEventListener('submit', () => {
        if (resolved.beforeSubmit) {
            resolved.beforeSubmit();
        }

        if (resolved.sourceFormId && resolved.inputFieldId) {
            const sourceForm = document.getElementById(resolved.sourceFormId) as HTMLFormElement | null;
            const inputField = document.getElementById(resolved.inputFieldId) as HTMLInputElement | null;
            if (sourceForm && inputField) {
                inputField.value = JSON.stringify(collectFormData(sourceForm));
            }
        }

        if (btn) {
            setButtonState(btn, true, resolved);
        }

        const timeoutId = setTimeout(() => {
            if (btn) {
                setButtonState(btn, false, resolved);
            }
        }, resolved.timeoutMs);

        const onLoad = (): void => {
            iframe.removeEventListener('load', onLoad);
            clearTimeout(timeoutId);

            try {
                const iframeWindow = iframe.contentWindow;
                if (iframeWindow && iframeWindow.document && iframeWindow.document.body.innerHTML.length > 0) {
                    iframeWindow.focus();
                    iframeWindow.print();
                }
            } catch {
                /* Cross-origin or security restriction — fall back silently. */
            }

            if (btn) {
                setButtonState(btn, false, resolved);
            }
        };

        iframe.addEventListener('load', onLoad);
    });
}
